'use client';

import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import { useAccessibleNameWarning } from '@/lib/hooks/util/useAccessibleNameWarning';
import { cn } from '@/lib/utils/tailwind';

import { HeroIcon } from './../Icon/HeroIcon';
import { Typography } from './../Typography';

import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';

export type VariantType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'error'
  | 'ghost';
export type SizeType = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
const validVariants: VariantType[] = [
  'primary',
  'secondary',
  'tertiary',
  'error',
  'ghost',
];
const validSizes: SizeType[] = ['sm', 'md', 'lg', 'xl', '2xl'];
export const buttonBaseClasses: Record<VariantType, string[]> = {
  primary: [
    'rounded-(--button-primary-radius-default)',
    'bg-(--button-primary-background-default)',
    'text-(--button-primary-text-color-default)',
    'hover:bg-(--button-primary-background-hover)',
    'hover:text-(--button-primary-text-color-hover)',
    'disabled:bg-(--button-primary-background-disabled)',
    'disabled:text-(--button-primary-text-color-disabled)',
    'aria-disabled:bg-(--button-primary-background-disabled)',
    'aria-disabled:text-(--button-primary-text-color-disabled)',
    'focus-visible:outline-offset-2',
    'drop-shadow-(--button-primary-shadow-color-default)',
  ],
  secondary: [
    'rounded-(--button-secondary-radius-default)',
    'bg-(--button-secondary-background-default)',
    'text-(--button-secondary-text-color-default)',
    'hover:bg-(--button-secondary-background-hover)',
    'hover:text-(--button-secondary-text-color-hover)',
    'disabled:bg-(--button-secondary-background-disabled)',
    'disabled:text-(--button-secondary-text-color-disabled)',
    'aria-disabled:bg-(--button-secondary-background-disabled)',
    'aria-disabled:text-(--button-secondary-text-color-disabled)',
    'focus-visible:outline-offset-1',
    'drop-shadow-(--button-secondary-shadow-color-default)',
  ],
  tertiary: [
    'rounded-(--button-tertiary-radius-default)',
    'bg-(--button-tertiary-background-default)',
    'text-(--button-tertiary-text-color-default)',
    'hover:bg-(--button-tertiary-background-hover)',
    'hover:text-(--button-tertiary-text-color-hover)',
    'aria-disabled:bg-(--button-tertiary-background-default)',
    'aria-disabled:text-(--button-tertiary-text-color-default)',
    'drop-shadow-(--button-tertiary-shadow-color-default)',
    'focus-visible:outline-offset-1',
  ],
  error: [
    'rounded-(--button-error-radius-default)',
    'bg-(--button-error-background-default)',
    'text-(--button-error-text-color-default)',
    'hover:bg-(--button-error-background-hover)',
    'disabled:bg-(--button-error-background-disabled)',
    'disabled:text-(--button-error-text-color-disabled)',
    'aria-disabled:bg-(--button-error-background-disabled)',
    'aria-disabled:text-(--button-error-text-color-disabled)',
    'focus-visible:outline-offset-2',
    'drop-shadow-(--button-error-shadow-color-default)',
  ],
  ghost: [
    'rounded-(--button-ghost-radius-default)',
    'bg-(--button-ghost-background-default)',
    'text-(--button-ghost-text-color-default)',
    'hover:bg-(--button-ghost-background-hover)',
    'hover:text-(--button-ghost-text-color-hover)',
    'disabled:text-(--button-ghost-text-color-disabled) disabled:opacity-50',
    'aria-disabled:bg-(--button-ghost-background-default)',
    'aria-disabled:text-(--button-ghost-text-color-disabled) aria-disabled:opacity-50',
    'drop-shadow-(--button-ghost-shadow-color-default)',
    'focus-visible:outline-offset-1',
  ],
};
export const sizeCompoundMap: Record<VariantType, Record<SizeType, string>> = {
  primary: {
    sm: 'px-(--button-primary-hspacing-xs) py-(--button-primary-vspacing-xs)',
    md: 'px-(--button-primary-hspacing-sm) py-(--button-primary-vspacing-sm)',
    lg: 'px-(--button-primary-hspacing-md) py-(--button-primary-vspacing-md)',
    xl: 'px-(--button-primary-hspacing-lg) py-(--button-primary-vspacing-lg)',
    '2xl':
      'px-(--button-primary-hspacing-xl) py-(--button-primary-vspacing-xl)',
  },
  secondary: {
    sm: 'px-(--button-secondary-hspacing-xs) py-(--button-secondary-vspacing-xs)',
    md: 'px-(--button-secondary-hspacing-sm) py-(--button-secondary-vspacing-sm)',
    lg: 'px-(--button-secondary-hspacing-md) py-(--button-secondary-vspacing-md)',
    xl: 'px-(--button-secondary-hspacing-lg) py-(--button-secondary-vspacing-lg)',
    '2xl':
      'px-(--button-secondary-hspacing-xl) py-(--button-secondary-vspacing-xl)',
  },
  tertiary: {
    sm: 'px-(--button-tertiary-hspacing-xs) py-(--button-tertiary-vspacing-xs)',
    md: 'px-(--button-tertiary-hspacing-sm) py-(--button-tertiary-vspacing-sm)',
    lg: 'px-(--button-tertiary-hspacing-md) py-(--button-tertiary-vspacing-md)',
    xl: 'px-(--button-tertiary-hspacing-lg) py-(--button-tertiary-vspacing-lg)',
    '2xl':
      'px-(--button-tertiary-hspacing-xl) py-(--button-tertiary-vspacing-xl)',
  },
  error: {
    sm: 'px-(--button-error-hspacing-xs) py-(--button-error-vspacing-xs)',
    md: 'px-(--button-error-hspacing-sm) py-(--button-error-vspacing-sm)',
    lg: 'px-(--button-error-hspacing-md) py-(--button-error-vspacing-md)',
    xl: 'px-(--button-error-hspacing-lg) py-(--button-error-vspacing-lg)',
    '2xl': 'px-(--button-error-hspacing-xl) py-(--button-error-vspacing-xl)',
  },
  ghost: {
    sm: 'px-(--button-ghost-hspacing-xs) py-(--button-ghost-vspacing-xs)',
    md: 'px-(--button-ghost-hspacing-sm) py-(--button-ghost-vspacing-sm)',
    lg: 'px-(--button-ghost-hspacing-md) py-(--button-ghost-vspacing-md)',
    xl: 'px-(--button-ghost-hspacing-lg) py-(--button-ghost-vspacing-lg)',
    '2xl': 'px-(--button-ghost-hspacing-xl) py-(--button-ghost-vspacing-xl)',
  },
};
export const generateCompoundVariants = () => {
  const variants: { variant: VariantType; size: SizeType; class: string }[] =
    [];
  for (const variant of validVariants) {
    for (const size of validSizes) {
      variants.push({ variant, size, class: sizeCompoundMap[variant][size] });
    }
  }
  return variants;
};
export const buttonVariants = cva(
  [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-(--button-primary-hspacing-default)',
    'relative cursor-pointer',
    'min-h-6 min-w-6',
    'focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-(--focus-ring-color)',
    'disabled:pointer-events-none',
    'aria-disabled:cursor-not-allowed',
    'font-(--font-buttons)',
    'text-(length:--text-button--line-height)',
    '[text-transform:var(--text-button-transform)]',
    '[font-style:var(--text-button-style)]',
  ],
  {
    variants: {
      variant: {
        primary: buttonBaseClasses.primary,
        secondary: buttonBaseClasses.secondary,
        tertiary: buttonBaseClasses.tertiary,
        error: buttonBaseClasses.error,
        ghost: buttonBaseClasses.ghost,
      },
      size: {
        sm: 'text-(length:--text-button-sm) font-(--font-weight-button-sm)',
        md: 'text-(length:--text-button-md) font-(--font-weight-button-md)',
        lg: 'text-(length:--text-button-lg) font-(--font-weight-button-lg)',
        xl: 'text-(length:--text-button-xl) font-(--font-weight-button-lg)',
        '2xl': 'text-(length:--text-button-xl) font-(--font-weight-button-lg)',
      },
    },
    compoundVariants: generateCompoundVariants(),
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);
const buttonTextSize: Record<SizeType, SizeType> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
  '2xl': '2xl',
};
export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  size?: SizeType;
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}
export const Button = ({
  variant = 'primary',
  size = 'md',
  isLoading,
  children,
  className,
  onClick,
  disabled,
  'aria-disabled': ariaDisabled,
  ref,
  ...props
}: ButtonProps) => {
  const t = useTranslations();
  const accessibleNameRef = useAccessibleNameWarning('Button');
  const composedRef = useComposedRefs<HTMLButtonElement>(
    accessibleNameRef,
    ref,
  );
  const isInactive =
    Boolean(isLoading) || ariaDisabled === true || ariaDisabled === 'true';

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (isInactive) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      ref={composedRef}
      type="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
      onClick={handleClick}
      disabled={disabled}
      aria-disabled={disabled || isInactive || undefined}
      aria-busy={isLoading || undefined}
    >
      {isLoading ? (
        <>
          <HeroIcon icon={ArrowPathIcon} className="h-5 w-5 animate-spin" />
          <Typography.Text
            as="span"
            variant="body"
            size={buttonTextSize[size]}
            weight="medium"
          >
            {t('BUTTON_TITLE_LOADING')}
          </Typography.Text>
        </>
      ) : (
        children
      )}
    </button>
  );
};
